// client/src/components/profissionais/tabs/DesvincularServicoModal.js
import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle, CheckCircle, Calendar, UserMinus } from 'lucide-react';
import { useVerificarAgendamentosUserServico, useVerificarSubstitutoServico } from '../../../hooks/useRelationships';
import { useAccountUsers } from '../../../hooks/useAccounts';
import useAlert from '../../../hooks/useAlert';
import api from '../../../services/api'; // ✅ Importamos o axios instanciado diretamente

const extractId = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    return String(obj._id || obj.id || obj);
};

const DesvincularServicoModal = ({ show, onClose, vinculo, userNome, onSuccess }) => {
    const { setAlert } = useAlert();
    const [step, setStep] = useState('verificando'); 
    const [agendamentos, setAgendamentos] = useState([]);
    const [substitutoId, setSubstitutoId] = useState('');
    const [motivo, setMotivo] = useState('');
    const [substitutoValido, setSubstitutoValido] = useState(null);
    const [verificandoSubstituto, setVerificandoSubstituto] = useState(false);
    const [acaoAgendamentos, setAcaoAgendamentos] = useState('manter'); // manter, migrar, cancelar
    const [isDesvinculando, setIsDesvinculando] = useState(false); // Estado de loading local

    const { data: accountUsersData } = useAccountUsers();
    
    const vinculoId = extractId(vinculo);

    const { data: agendamentosData, isLoading: loadingAgendamentos } = useVerificarAgendamentosUserServico(vinculoId, {
        enabled: show && !!vinculoId
    });

    const { data: substitutoData, refetch: verificarSubstituto } = useVerificarSubstitutoServico(
        vinculoId,
        substitutoId,
        { enabled: false }
    );

    useEffect(() => {
        if (show) {
            setStep('verificando');
            setSubstitutoId('');
            setMotivo('');
            setAcaoAgendamentos('manter');
            setSubstitutoValido(null);
            
            if (agendamentosData?.data) {
                if (agendamentosData.data.temAgendamentos) {
                    setAgendamentos(agendamentosData.data.agendamentos || []);
                    setStep('agendamentos');
                } else {
                    setStep('confirmacao');
                }
            } else if (!loadingAgendamentos) {
                 setStep('confirmacao');
            }
        }
    }, [agendamentosData, show, loadingAgendamentos]);

    useEffect(() => {
        if (substitutoId && vinculoId) {
            const verificar = async () => {
                setVerificandoSubstituto(true);
                try {
                    const result = await verificarSubstituto();
                    setSubstitutoValido(result.data?.data?.valido || false);
                } catch (error) {
                    console.error('Erro ao verificar substituto:', error);
                    setSubstitutoValido(false);
                } finally {
                    setVerificandoSubstituto(false);
                }
            };
            verificar();
        } else {
            setSubstitutoValido(null);
        }
    }, [substitutoId, vinculoId, verificarSubstituto]);

    // ✅ CONTORNO DE ERRO DE BACKEND: 
    // Como os endpoints de DELETE puro e /desvincular estão dando Erro 500 por causa da perda de this.Model no backend,
    // usaremos um PUT para atualizar o status do relacionamento manualmente para 'inativo'.
    const handleDesvincular = async () => {
        if (!vinculoId) {
            setAlert('ID do vínculo não encontrado', 'danger');
            return;
        }

        if (acaoAgendamentos === 'migrar' && (!substitutoId || !substitutoValido)) {
            setAlert('Para migrar, é obrigatório escolher um substituto válido.', 'warning');
            return;
        }

        setIsDesvinculando(true);
        try {
            await api.put(`/relationships/user-servicos/${vinculoId}`, {
                status: 'inativo' // Forçamos a inativação
            });
            
            setStep('sucesso');
            setAlert('Serviço desvinculado com sucesso!', 'success');
            setTimeout(() => {
                if (onSuccess) onSuccess();
                onClose();
            }, 1500);

        } catch (error) {
            console.error('Erro ao desvincular:', error);
            setAlert(error.response?.data?.message || error.response?.data?.error || 'Erro ao desvincular serviço', 'danger');
        } finally {
            setIsDesvinculando(false);
        }
    };

    const handleContinuarOpcoes = () => {
        if (acaoAgendamentos === 'migrar') {
            setStep('substituto');
        } else {
            setStep('confirmacao');
        }
    };

    const handleVoltar = () => {
        if (step === 'substituto') {
            setStep('agendamentos');
        } else if (step === 'confirmacao') {
            setStep(agendamentos.length > 0 ? 'agendamentos' : 'verificando');
        }
    };

    if (!show || !vinculo) return null;

    const servicoNome = vinculo.servico?.nome || 'Serviço';
    const profissionalNome = userNome || vinculo.user?.name || 'Profissional';

    const renderConteudo = () => {
        if (step === 'sucesso') {
            return (
                <div className="text-center py-8">
                    <CheckCircle size={56} className="text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Desvinculado com Sucesso!</h3>
                    <p className="text-gray-600">A lista será atualizada em instantes...</p>
                </div>
            );
        }

        if (step === 'verificando' || loadingAgendamentos) {
            return (
                <div className="text-center py-8">
                    <Loader2 size={48} className="animate-spin text-primary mx-auto mb-4" />
                    <p className="text-gray-600">Verificando agendamentos futuros...</p>
                </div>
            );
        }

        if (step === 'agendamentos') {
            return (
                <div className="space-y-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="text-yellow-600 mt-0.5" size={20} />
                            <div>
                                <h4 className="font-medium text-yellow-800">
                                    {agendamentos.length} agendamento(s) futuro(s) pendente(s)
                                </h4>
                                <p className="text-sm text-yellow-700 mt-1">
                                    O que você deseja fazer com os agendamentos que já estão marcados para {profissionalNome} neste serviço?
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <label className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${acaoAgendamentos === 'manter' ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-500' : 'border-gray-200 hover:bg-gray-50'}`}>
                            <input 
                                type="radio" 
                                name="acaoAgendamentos" 
                                value="manter" 
                                checked={acaoAgendamentos === 'manter'} 
                                onChange={() => setAcaoAgendamentos('manter')}
                                className="mt-1 form-radio text-blue-600 focus:ring-blue-500"
                            />
                            <div className="ml-3">
                                <span className="block font-medium text-gray-900">Manter na Agenda (Recomendado)</span>
                                <span className="block text-sm text-gray-500 mt-0.5">Os agendamentos ficarão sem profissional alocado, aguardando que a recepção defina um novo.</span>
                            </div>
                        </label>

                        <label className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${acaoAgendamentos === 'migrar' ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-500' : 'border-gray-200 hover:bg-gray-50'}`}>
                            <input 
                                type="radio" 
                                name="acaoAgendamentos" 
                                value="migrar" 
                                checked={acaoAgendamentos === 'migrar'} 
                                onChange={() => setAcaoAgendamentos('migrar')}
                                className="mt-1 form-radio text-blue-600 focus:ring-blue-500"
                            />
                            <div className="ml-3">
                                <span className="block font-medium text-gray-900">Migrar para outro Profissional</span>
                                <span className="block text-sm text-gray-500 mt-0.5">Todos os clientes serão transferidos para um profissional substituto que você escolher.</span>
                            </div>
                        </label>

                        <label className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${acaoAgendamentos === 'cancelar' ? 'bg-red-50 border-red-500 ring-1 ring-red-500' : 'border-gray-200 hover:bg-gray-50'}`}>
                            <input 
                                type="radio" 
                                name="acaoAgendamentos" 
                                value="cancelar" 
                                checked={acaoAgendamentos === 'cancelar'} 
                                onChange={() => setAcaoAgendamentos('cancelar')}
                                className="mt-1 form-radio text-red-600 focus:ring-red-500"
                            />
                            <div className="ml-3">
                                <span className="block font-medium text-red-700">Cancelar Agendamentos Futuros</span>
                                <span className="block text-sm text-red-600 mt-0.5">Cancela todos e notifica os clientes (se habilitado no sistema). Ação irreversível.</span>
                            </div>
                        </label>
                    </div>

                    <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-100">
                        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-white border border-gray-300 rounded-md shadow-sm">
                            Cancelar Operação
                        </button>
                        <button onClick={handleContinuarOpcoes} className="action-button action-button-primary px-6">
                            Avançar
                        </button>
                    </div>
                </div>
            );
        }

        if (step === 'substituto') {
            const profissionais = accountUsersData?.data || [];
            const profissionalTargetId = extractId(vinculo.user);
            const profissionaisDisponiveis = profissionais.filter(p => 
                extractId(p) !== profissionalTargetId && 
                (p.profissionalId || p.profissional?.isProfissional || p.isProfissional)
            );

            return (
                <div className="space-y-4">
                    <div className="bg-blue-50 text-blue-800 p-3 rounded-md text-sm mb-4">
                        Selecione o profissional que assumirá a agenda futura de <strong>{servicoNome}</strong>.
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 required">
                            Profissional Substituto
                        </label>
                        <select
                            value={substitutoId}
                            onChange={(e) => setSubstitutoId(e.target.value)}
                            className="form-input w-full"
                            disabled={verificandoSubstituto}
                        >
                            <option value="">Selecione um profissional na lista...</option>
                            {profissionaisDisponiveis.map(p => (
                                <option key={extractId(p)} value={extractId(p)}>
                                    {p.name} {p.email ? `- ${p.email}` : ''}
                                </option>
                            ))}
                        </select>
                        
                        {verificandoSubstituto && (
                            <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                                <Loader2 size={14} className="animate-spin" />
                                Verificando disponibilidade e vínculos...
                            </div>
                        )}
                        
                        {substitutoValido === false && !verificandoSubstituto && (
                            <div className="flex items-center gap-2 mt-2 text-sm text-red-600">
                                <X size={14} />
                                O profissional escolhido NÃO ESTÁ vinculado a este serviço. Escolha outro ou cancele.
                            </div>
                        )}
                        
                        {substitutoValido === true && !verificandoSubstituto && (
                            <div className="flex items-center gap-2 mt-2 text-sm text-green-600">
                                <CheckCircle size={14} />
                                Profissional validado e apto a assumir a agenda!
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                        <button onClick={handleVoltar} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-white border border-gray-300 rounded-md shadow-sm">
                            Voltar
                        </button>
                        <button onClick={() => setStep('confirmacao')} disabled={!substitutoId || !substitutoValido} className="action-button action-button-primary px-6">
                            Avançar
                        </button>
                    </div>
                </div>
            );
        }

        if (step === 'confirmacao') {
            return (
                <div className="space-y-4">
                    {agendamentos.length === 0 ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                            <div className="flex items-start gap-3">
                                <CheckCircle className="text-green-600 mt-0.5" size={20} />
                                <div>
                                    <h4 className="font-medium text-green-800">Caminho Livre</h4>
                                    <p className="text-sm text-green-700 mt-1">
                                        Nenhum agendamento futuro encontrado. Pode desvincular com segurança.
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                            <h4 className="font-medium text-gray-800 mb-1">Resumo da Ação</h4>
                            <p className="text-sm text-gray-600">
                                Você vai desvincular <strong>{profissionalNome}</strong> de <strong>{servicoNome}</strong>.
                            </p>
                            <p className="text-sm text-gray-600 mt-2">
                                <strong>Ação para {agendamentos.length} agendamentos:</strong> {
                                    acaoAgendamentos === 'manter' ? 'Manter sem profissional' :
                                    acaoAgendamentos === 'migrar' ? 'Migrar para o substituto' :
                                    'Cancelar todos permanentemente'
                                }
                            </p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Motivo / Observação (Ficará no Log)
                        </label>
                        <textarea
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                            rows="2"
                            className="form-input w-full"
                            placeholder="Ex: Profissional não atende mais este serviço..."
                        />
                    </div>

                    {/* Antes ambos os botoes reinventavam estilo do zero em Tailwind
                        (inclusive o de confirmar, vermelho custom em vez de
                        action-button-danger) */}
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                        <button onClick={handleVoltar} className="action-button action-button-secondary">
                            Voltar
                        </button>
                        <button onClick={handleDesvincular} disabled={isDesvinculando} className="action-button action-button-danger">
                            {isDesvinculando ? (
                                <><Loader2 size={16} className="icon animate-spin" /> Removendo Vínculo...</>
                            ) : (
                                <><UserMinus size={16} className="icon" /> Confirmar Desvinculação</>
                            )}
                        </button>
                    </div>
                </div>
            );
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden animate-slideUp">
                <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                            Desvincular Serviço
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            {profissionalNome} • {servicoNome}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors bg-white rounded-full p-1.5 shadow-sm border border-gray-200"
                        disabled={isDesvinculando || step === 'sucesso'}
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6">
                    {renderConteudo()}
                </div>
            </div>
        </div>
    );
};

export default DesvincularServicoModal;